import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import Chart from "chart.js/auto";
import type { ChartConfiguration } from "chart.js";
import { Widget } from "../../models/dashboard.model";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { WidgetPeriodFiltersComponent } from "../../../../shared/components/widget-period-filters/widget-period-filters.component";
import { SharedModule } from "../../../../shared/shared.module";
import { LignesFacadeService } from "../../../ligne/services/lignes/lignes-facade.service";
import { ArticlesInfosFacadeService } from "../../../articles/services/article-infos/article-infos-facade.service";
import { WidgetHelperService } from "../../services/widget-helper.service";
import { AideContextComponent } from "../../../../shared/components/aide-context/aide-context.component";
import { AideContextSection } from "../../../../shared/models/aide-context.model";
import { TranslateUtils } from "../../../../shared/utils/translate.utils";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { LoaderSkeletonComponent } from "../../../../shared/loader-skeleton/loader-skeleton.component";
import { format } from "date-fns";
import { parseNumber } from "../../../../shared/utils/number-utils.service";
import { OF } from "../../../of/models/of.model";

const MONTHS_FR = [
  "Jan", "Fév", "Mar", "Avr", "Mai", "Jun",
  "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc",
];

@Component({
  selector: "vl-widget-quantite-produite-ligne",
  standalone: true,
  imports: [
    CommonModule,
    WidgetTitleComponent,
    WidgetPeriodFiltersComponent,
    SharedModule,
    AideContextComponent,
    ChipsToggleComponent,
    LoaderSkeletonComponent,
  ],
  templateUrl: "./widget-quantite-produite-ligne.component.html",
  styleUrls: ["./widget-quantite-produite-ligne.component.scss"],
  host: { class: "widget-component" },
})
export class WidgetQuantiteProduiteLigneComponent {
  widget = input<Widget>();
  metadata = input<EntityMetadata>();

  private ligneFacade = inject(LignesFacadeService);
  private articleInfosFacade = inject(ArticlesInfosFacadeService);
  private elementRef = inject(ElementRef);
  private destroyRef = inject(DestroyRef);
  stats = this.ligneFacade.getState().stats;

  canvasRef = viewChild<ElementRef<HTMLCanvasElement>>("qteProdChart");
  chart?: Chart;

  aideSections: AideContextSection[] = [
    { type: "text", html: TranslateUtils.translate("stats_ligne.aide_quantite_produite") },
    { type: "highlight", title: TranslateUtils.translate("stats_ligne.calcul"), html: TranslateUtils.translate("stats_ligne.aide_quantite_produite_formule") },
  ];

  period = signal<string>("m");
  timeScale = signal<string>("MAX");
  comparison = signal<string | null>(null);
  selectedUnite = signal<string>("ALL");

  /** Map articleId → unite denomination, rebuilt when stats change */
  private uniteMap = computed(() => {
    const ofs: OF[] = this.stats()?.ofs ?? [];
    const map = new Map<string, string>();
    for (const of of ofs) {
      const articleId = of.articleId;
      if (!articleId || map.has(articleId)) continue;
      const denom = this.articleInfosFacade.getByArticleId(articleId)?.()?.unite?.denomination ?? "";
      map.set(articleId, denom);
    }
    return map;
  });

  uniteElements = computed(() => {
    const unites = new Set(this.uniteMap().values());
    unites.delete("");
    const sorted = [...unites].sort();
    return [
      { name: TranslateUtils.translate("stats_ligne.toutes"), value: "ALL" },
      ...sorted.map((u) => ({ name: u, value: u })),
    ];
  });

  private static UNITE_COLORS = [
    "hsl(210 70% 55%)", "hsl(35 90% 55%)", "hsl(140 70% 45%)",
    "hsl(280 60% 55%)", "hsl(0 70% 55%)", "hsl(170 60% 45%)",
    "hsl(45 90% 50%)", "hsl(320 60% 55%)",
  ];

  chartData = computed(() => {
    const allOfs: OF[] = this.stats()?.ofs ?? [];
    const p = this.period();
    const ts = this.timeScale();
    const comp = this.comparison();
    const unite = this.selectedUnite();
    const uMap = this.uniteMap();

    const ofs = unite === "ALL"
      ? allOfs
      : allOfs.filter((of) => uMap.get(of.articleId) === unite);

    const emptyMulti = { mode: "multi" as const, labels: [] as string[], datasets: [] as { label: string; data: number[]; backgroundColor: string; borderRadius: number }[], unite, compMode: comp };
    const empty = { mode: "single" as const, labels: [] as string[], values: [] as number[], comparison: [] as number[], comparisonLabels: [] as string[], unite, compMode: comp };
    if (!ofs.length) return unite === "ALL" && !comp ? emptyMulti : empty;

    // Mode "Toutes" sans comparaison → stacked bars par unité
    if (unite === "ALL" && !comp) {
      const unites = [...new Set([...uMap.values()].filter(Boolean))].sort();
      if (!unites.length) return emptyMulti;

      const keySet = new Set<string>();
      for (const of of allOfs) {
        if (!of.dateFin) continue;
        keySet.add(this.dateKey(new Date(of.dateFin), p));
      }
      const allKeys = [...keySet].sort();
      const filtered = this.filterByScale(allKeys.map((k) => [k, 0] as [string, number]), ts).map(([k]) => k);
      if (!filtered.length) return emptyMulti;

      const datasets = unites.map((u, i) => {
        const grouped = new Map<string, number>();
        for (const of of allOfs) {
          if (!of.dateFin || uMap.get(of.articleId) !== u) continue;
          const key = this.dateKey(new Date(of.dateFin), p);
          if (!filtered.includes(key)) continue;
          grouped.set(key, (grouped.get(key) ?? 0) + parseNumber(of.quantiteProduite ?? 0));
        }
        return {
          label: u,
          data: filtered.map((k) => grouped.get(k) ?? 0),
          backgroundColor: WidgetQuantiteProduiteLigneComponent.UNITE_COLORS[i % WidgetQuantiteProduiteLigneComponent.UNITE_COLORS.length],
          borderRadius: 4,
        };
      });

      return {
        mode: "multi" as const,
        labels: filtered.map((k) => this.formatLabel(k)),
        datasets,
        unite,
        compMode: comp,
      };
    }

    const grouped = new Map<string, number>();
    for (const of of ofs) {
      if (!of.dateFin) continue;
      const date = new Date(of.dateFin);
      const key = this.dateKey(date, p);
      grouped.set(key, (grouped.get(key) ?? 0) + parseNumber(of.quantiteProduite ?? 0));
    }

    const entries = [...grouped.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    const filtered = this.filterByScale(entries, ts);

    const labels = filtered.map(([k]) => this.formatLabel(k));
    const values = filtered.map(([, v]) => v);

    if (!comp || ts === "MAX") {
      return { mode: "single" as const, labels, values, comparison: [] as number[], comparisonLabels: [] as string[], unite, compMode: comp };
    }

    // Compute comparison date range
    const now = new Date();
    const refDate = this.getRefDate(ts);
    let compStart: Date;
    let compEnd: Date;

    if (comp === "n-1") {
      compStart = new Date(refDate);
      compStart.setFullYear(compStart.getFullYear() - 1);
      compEnd = new Date(now);
      compEnd.setFullYear(compEnd.getFullYear() - 1);
    } else {
      const duration = now.getTime() - refDate.getTime();
      compStart = new Date(refDate.getTime() - duration);
      compEnd = new Date(refDate);
    }

    // Group comparison OFs
    const compGrouped = new Map<string, number>();
    for (const of of ofs) {
      if (!of.dateFin) continue;
      const date = new Date(of.dateFin);
      if (date < compStart || date >= compEnd) continue;
      const key = this.dateKey(date, p);
      compGrouped.set(key, (compGrouped.get(key) ?? 0) + parseNumber(of.quantiteProduite ?? 0));
    }

    // Align comparison with current labels via relative keys
    const compMap = new Map<string, { value: number; key: string }>();
    for (const [key, value] of compGrouped) {
      const relKey = this.relativeKey(key, p);
      compMap.set(relKey, { value, key });
    }

    const comparison = filtered.map(([k]) => compMap.get(this.relativeKey(k, p))?.value ?? 0);
    const comparisonLabels = filtered.map(([k]) => {
      const entry = compMap.get(this.relativeKey(k, p));
      return entry ? this.formatLabel(entry.key) : "";
    });

    return { mode: "single" as const, labels, values, comparison, comparisonLabels, unite, compMode: comp };
  });

  constructor() {
    this.destroyRef.onDestroy(() => this.destroyChart());

    effect(() => {
      const canvas = this.canvasRef();
      const data = this.chartData();
      if (!canvas || !data.labels.length) {
        this.destroyChart();
        return;
      }
      this.buildChart(data);
    });
  }

  private dateKey(d: Date, period: string): string {
    switch (period) {
      case "j":
        return format(d, "yyyy-MM-dd");
      case "s":
        return format(d, "yyyy-'S'ww");
      case "a":
        return format(d, "yyyy");
      case "m":
      default:
        return format(d, "yyyy-MM");
    }
  }

  private formatLabel(key: string): string {
    const m = key.match(/^(\d{4})-(\d{2})$/);
    if (m) return `${MONTHS_FR[parseInt(m[2], 10) - 1]} ${m[1].slice(2)}`;
    const s = key.match(/^\d{4}-S(\d{2})$/);
    if (s) return `S${s[1]}`;
    const d = key.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (d) return `${d[3]}/${d[2]}`;
    return key;
  }

  private getRefDate(scale: string): Date {
    const now = new Date();
    switch (scale) {
      case "1 m":
        return new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
      case "6 m":
        return new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
      case "1 a":
        return new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
      case "5 a":
        return new Date(now.getFullYear() - 5, now.getMonth(), now.getDate());
      case "YTD":
      default:
        return new Date(now.getFullYear(), 0, 1);
    }
  }

  private relativeKey(key: string, period: string): string {
    switch (period) {
      case "m":
        return key.split("-")[1] ?? key;       // "yyyy-MM" → "MM"
      case "j":
        return key.substring(5);               // "yyyy-MM-dd" → "MM-dd"
      case "s":
        return key.substring(5);               // "yyyy-Sww" → "Sww"
      case "a":
      default:
        return key;
    }
  }

  private filterByScale(
    entries: [string, number][],
    scale: string,
  ): [string, number][] {
    if (!entries.length) return entries;
    const now = new Date();
    let cutoff: Date;
    switch (scale) {
      case "1 m":
        cutoff = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        break;
      case "6 m":
        cutoff = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
        break;
      case "1 a":
        cutoff = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        break;
      case "5 a":
        cutoff = new Date(now.getFullYear() - 5, now.getMonth(), now.getDate());
        break;
      case "MAX":
        return entries;
      case "YTD":
      default:
        cutoff = new Date(now.getFullYear(), 0, 1);
        break;
    }
    const cutKey = this.dateKey(cutoff, this.period());
    return entries.filter(([k]) => k >= cutKey);
  }

  private destroyChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
  }

  /** Ensure color is in hex format so appending alpha like "60" works */
  private ensureHex(color: string): string {
    if (color.startsWith("#")) return color;
    const ctx = document.createElement("canvas").getContext("2d");
    if (!ctx) return color;
    ctx.fillStyle = color;
    return ctx.fillStyle; // browsers normalize to hex
  }

  private buildChart(data: any) {
    const canvas = this.canvasRef();
    if (!canvas) return;
    const ctx = canvas.nativeElement.getContext("2d");
    if (!ctx) return;

    this.destroyChart();

    const style = getComputedStyle(this.elementRef.nativeElement);
    const barColor = style.getPropertyValue("--color-on-accent-alt").trim() || "hsl(210 70% 55%)";
    const textColor = style.getPropertyValue("--color-on-background-alt").trim() || "#888";

    const isMulti = data.mode === "multi";

    let datasets: any[];
    if (isMulti) {
      datasets = data.datasets;
    } else {
      const hexColor = this.ensureHex(barColor);
      const hasComparison = data.comparison?.length > 0;
      datasets = [
        {
          label: TranslateUtils.translate("stats_ligne.quantite_produite_chart"),
          data: data.values,
          backgroundColor: hexColor,
          borderRadius: 4,
        },
      ];
      if (hasComparison) {
        datasets.push({
          label: data.compMode === "n-1" ? TranslateUtils.translate("stats_ligne.n_moins_1") : TranslateUtils.translate("stats_ligne.prec"),
          data: data.comparison,
          backgroundColor: hexColor + "60",
          borderRadius: 4,
        });
      }
    }

    const hasComparison = !isMulti && data.comparison?.length > 0;
    const compLabels = data.comparisonLabels ?? [];
    const config: ChartConfiguration<"bar", number[], string> = {
      type: "bar",
      data: { labels: data.labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: {
          legend: {
            display: isMulti || hasComparison,
            position: "bottom",
            labels: { color: textColor, boxWidth: 12, padding: 8, font: { size: 11 } },
          },
          tooltip: {
            callbacks: {
              title: (items) => {
                const idx = items[0]?.dataIndex;
                const label = data.labels[idx] ?? "";
                if (hasComparison && compLabels[idx]) return `${label} vs ${compLabels[idx]}`;
                return label;
              },
              label: (ctx) => {
                const suffix = data.unite !== "ALL" ? ` ${data.unite}` : "";
                if (isMulti) {
                  return ` ${ctx.dataset.label}: ${ctx.parsed.y.toLocaleString("fr-FR")}${suffix}`;
                }
                const periodLabel = ctx.datasetIndex === 1 && compLabels[ctx.dataIndex] ? ` (${compLabels[ctx.dataIndex]})` : "";
                return ` ${ctx.dataset.label}${periodLabel}: ${ctx.parsed.y.toLocaleString("fr-FR")}${suffix}`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: textColor, maxRotation: 45, font: { size: 10 } },
          },
          y: {
            beginAtZero: true,
            ticks: {
              color: textColor,
              font: { size: 10 },
              callback: (value) => {
                const suffix = data.unite !== "ALL" ? ` ${data.unite}` : "";
                return WidgetHelperService.formatNumeric(+value) + suffix;
              },
            },
          },
        },
      },
    };
    this.chart = new Chart(ctx, config);
  }
}
